// HTTP клиент для работы с backend API
import { resolveApiBaseUrl, API_CONSTANTS } from '@minibook/shared';
import { DEFAULT_API_URL } from '../constants/booking.js';

const API_BASE_URL = resolveApiBaseUrl({
  apiUrl: (import.meta as any).env?.VITE_API_URL,
  isDev: !!(import.meta as any).env?.DEV,
  devFallbackUrl: DEFAULT_API_URL,
  appendApiV1: true,
});

const REQUEST_TIMEOUT_MS = API_CONSTANTS.BOOKING_REQUEST_TIMEOUT_MS;

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: any;
    retryAfterSec?: number;
  };
  requestId?: string | null;
}

async function fetchWithTimeout(url: string, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const externalSignal = init.signal;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } catch (fetchError) {
    if (fetchError instanceof Error && fetchError.name === 'AbortError') {
      throw {
        error: {
          code: 'request_timeout',
          message: 'Request timed out',
        },
        requestId: null,
      } as ApiError;
    }
    throw fetchError;
  } finally {
    clearTimeout(timeoutId);
  }
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetchWithTimeout(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const requestId = response.headers.get('x-request-id');
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.toLowerCase().includes('application/json');

    let data: any = null;
    if (isJson) {
      try {
        data = await response.json();
      } catch {
        throw {
          error: {
            code: 'invalid_json',
            message: 'Server returned invalid JSON',
          },
          requestId,
        } as ApiError;
      }
    } else {
      // Пустой ответ (например, 204) считаем валидным для успешных запросов.
      if (response.status === 204) {
        return {} as T;
      }
      const text = await response.text().catch(() => '');
      if (!response.ok) {
        throw {
          error: {
            code: 'invalid_response',
            message: text || `HTTP ${response.status}: ${response.statusText}`,
          },
          requestId,
        } as ApiError;
      }
      // Успешный non-JSON ответ (редкий кейс) возвращаем как есть.
      return text as unknown as T;
    }

    if (!response.ok) {
      // Обработка ошибок в формате { error: { code, message } }
      if (data.error && typeof data.error === 'object') {
        if (requestId) {
          (data as any).requestId = requestId;
        }
        throw data as ApiError;
      }
      // Fallback для других форматов ошибок
      throw {
        error: {
          code: 'unknown_error',
          message: data.message || `HTTP ${response.status}: ${response.statusText}`,
        },
        requestId,
      } as ApiError;
    }

    if (requestId && data && typeof data === 'object') {
      (data as any).requestId = requestId;
    }

    return data as T;
  }

  async get<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  async post<T>(endpoint: string, body?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  async put<T>(endpoint: string, body?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  async patch<T>(endpoint: string, body?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }
}

export const apiClient = new ApiClient();
